import type { InventoryPushChannel } from "@redotech/admin-sdk/rpc/schema/ims/get-inventory-push-settings";
import { ImsStatus } from "@redotech/crdb-enums/inventory/ims-status";
import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { PaymentProviderType } from "@redotech/redo-billing-common/billing-engine-types";
import { BillingPeriod } from "@redotech/redo-billing-common/service-invoice";
import { toast } from "@redotech/redo-web/alert";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { PageForm, PageFormSave } from "@redotech/redo-web/page-form";
import { Text } from "@redotech/redo-web/text";
import { groupInput, InputSchema } from "@redotech/ui/form";
import classNames from "classnames";
import { memo, useContext, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { RedoAdminRpcClientContext } from "../../app/redo-admin-rpc-client-provider";
import { RedoAdminClientContext } from "../../client/context";
import { getTeam } from "../../client/team";
import { LoadTeamContext, TeamContext } from "../team";
import * as teamCss from "../team.module.css";
import { CatalogCard } from "./ims/catalog-card";
import {
  ForecastingCostBreakdownCard,
  forecastingCostBreakdownForm,
} from "./ims/forecasting-cost-breakdown";
import {
  ImsGeneralCard,
  imsGeneralDefault,
  imsGeneralForm,
} from "./ims/general";
import {
  inventoryPushChannels,
  inventoryPushDefault,
  inventoryPushForm,
} from "./ims/inventory-push-toggles";
import { ShopifyBulkVariantEditCard } from "./ims/shopify-bulk-variant-edit-card";
import { useImsForecastingData } from "./ims/use-ims-forecasting-data";

export const ImsPage = memo(function ImsPage() {
  const params = useParams();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const teamId = params.teamId!;

  const team = useContext(TeamContext);
  const loadTeam = useRequiredContext(LoadTeamContext);

  const { data: forecastingData, isLoading } = useImsForecastingData(teamId);

  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const imsSettings = useLoad(
    () => rpcClient.getImsSettings({ teamId }),
    [rpcClient, teamId],
  );
  const inventoryPushSettings = useLoad(
    () => rpcClient.getInventoryPushSettings({ teamId }),
    [rpcClient, teamId],
  );

  const handleSave = useHandler(() => {
    loadTeam();
    // SKU counts are returned in the save response and updated in form state
  });

  const value: ImsValue | undefined = useMemo(() => {
    if (
      !team ||
      isLoading ||
      !forecastingData ||
      !imsSettings.value ||
      !inventoryPushSettings.value
    ) {
      return;
    }
    const shopifyPush = inventoryPushSettings.value.channels.find(
      (c) => c.channel === "shopify",
    );

    return {
      general: {
        enabled: !!team.settings.ims?.enabled,
        syncEnabled: !!team.settings.ims?.syncEnabled,
        catalogEnabled: !!team.settings.ims?.catalogEnabled,
        catalogSourceOfTruthStatus:
          team.settings.ims?.catalogSourceOfTruthStatus || ImsStatus.ReadOnly,
        forecastingEnabled: !!team.settings.ims?.forecastingEnabled,
        status: team.settings.ims?.status || ImsStatus.ReadOnly,
        imsBillingEnabled: !!team.settings.ims?.subscriptionBilling?.enabled,
        imsBillingRenewOnPeriodEnd:
          team.settings.ims?.subscriptionBilling?.renewOnPeriodEnd ?? true,
        imsBillingPriceInCents:
          team.settings.ims?.subscriptionBilling?.periodFeeInCents ?? 50_000,
        imsBillingPeriod:
          team.settings.ims?.subscriptionBilling?.period ?? "monthly",
        forecastingBillingEnabled:
          !!team.settings.ims?.forecastingBilling?.enabled,
        forecastingCustomPriceInCents:
          team.settings.ims?.forecastingBilling?.customPriceInCents ??
          team.settings.ims?.forecastingBilling?.periodFeeInCents ??
          50_000,
        forecastingBillingPeriod:
          team.settings.ims?.forecastingBilling?.period ?? "monthly",
        orderSyncTemporal: !!team.settings.ims?.orderSyncTemporal,
        handleNegativeOnHand: imsSettings.value.handleNegativeOnHand,
      },
      forecastingCostBreakdown: forecastingData,
      inventoryPush: { shopify: !!shopifyPush?.enabled },
    };
  }, [
    team,
    isLoading,
    forecastingData,
    imsSettings.value,
    inventoryPushSettings.value,
  ]);

  const inventoryPushConnected: Record<InventoryPushChannel, boolean> = {
    shopify: !!inventoryPushSettings.value?.channels.find(
      (c) => c.channel === "shopify",
    )?.connected,
  };

  const imsBillingStartDate =
    team?.settings.ims?.subscriptionBilling?.startDate;
  const selfEnroll = team?.settings.ims?.selfEnroll ?? undefined;

  return value ? (
    <ImsForm
      id={teamId}
      imsBillingStartDate={imsBillingStartDate}
      inventoryPushConnected={inventoryPushConnected}
      onSave={handleSave}
      selfEnroll={selfEnroll}
      value={value}
    />
  ) : null;
});

const imsForm = groupInput({
  general: imsGeneralForm,
  forecastingCostBreakdown: forecastingCostBreakdownForm,
  inventoryPush: inventoryPushForm,
});

type ImsValue = InputSchema.Value<typeof imsForm>;

const imsDefault: ImsValue = {
  general: imsGeneralDefault,
  forecastingCostBreakdown: {
    buckets: [
      { upperBound: "49", price: "3.99", currency: "USD", skuCount: 0 },
      { upperBound: "99", price: "3.79", currency: "USD", skuCount: 0 },
      { upperBound: "149", price: "3.59", currency: "USD", skuCount: 0 },
      { upperBound: "199", price: "3.39", currency: "USD", skuCount: 0 },
      { upperBound: "249", price: "3.19", currency: "USD", skuCount: 0 },
      { upperBound: "499", price: "2.89", currency: "USD", skuCount: 0 },
      { upperBound: "749", price: "2.59", currency: "USD", skuCount: 0 },
      { upperBound: "999", price: "2.49", currency: "USD", skuCount: 0 },
      { upperBound: "1499", price: "1.99", currency: "USD", skuCount: 0 },
      { upperBound: "1999", price: "1.49", currency: "USD", skuCount: 0 },
      { upperBound: "2999", price: "0.99", currency: "USD", skuCount: 0 },
      { upperBound: "3999", price: "0.89", currency: "USD", skuCount: 0 },
      { upperBound: "4999", price: "0.79", currency: "USD", skuCount: 0 },
      { upperBound: "7499", price: "0.59", currency: "USD", skuCount: 0 },
      { upperBound: "10000", price: "0.39", currency: "USD", skuCount: 0 },
      { upperBound: "25000", price: "0.19", currency: "USD", skuCount: 0 },
    ],
    catchAllSkuCount: 0,
    catchAllPrice: "0",
    catchAllCurrency: "USD",
  },
  inventoryPush: inventoryPushDefault,
};

export const ImsForm = memo(function ImsForm({
  id,
  onSave,
  value,
  imsBillingStartDate,
  selfEnroll,
  inventoryPushConnected,
}: {
  id: string;
  onSave: (value: ImsValue) => void;
  value: ImsValue | undefined;
  inventoryPushConnected: Record<InventoryPushChannel, boolean>;
  imsBillingStartDate?: Date | null;
  selfEnroll?: boolean;
}) {
  const client = useRequiredContext(RedoAdminClientContext);
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [showForecastingModal, setShowForecastingModal] = useState(false);
  const [showSotConfirmation, setShowSotConfirmation] = useState(false);

  const input = useInput(imsForm, value || imsDefault);

  const initialStatus = value?.general.status || ImsStatus.ReadOnly;
  const sotToggled =
    initialStatus === ImsStatus.ReadOnly &&
    input.value.general.status === ImsStatus.Pending;

  const doSave = useHandler(async (signal: AbortSignal) => {
    const team = await getTeam(client, { teamId: id, signal });
    const value = input.value;

    // Check if IMS subscription billing is being toggled on (from off to on)
    const wasImsBillingEnabled =
      !!team.settings.ims?.subscriptionBilling?.enabled;
    const isImsBillingNowEnabled = value.general.imsBillingEnabled;
    const isTogglingImsBillingOn =
      !wasImsBillingEnabled && isImsBillingNowEnabled;

    // Check if Forecasting billing is being toggled on (from off to on)
    const wasForecastingBillingEnabled =
      !!team.settings.ims?.forecastingBilling?.enabled;
    const isForecastingBillingNowEnabled =
      value.general.forecastingBillingEnabled;
    const isTogglingForecastingBillingOn =
      !wasForecastingBillingEnabled && isForecastingBillingNowEnabled;

    // Update or create subscriptionBilling with required fields
    const existingSubscriptionBilling = team.settings.ims?.subscriptionBilling;
    const subscriptionBilling = existingSubscriptionBilling
      ? {
          ...existingSubscriptionBilling,
          enabled: value.general.imsBillingEnabled,
          renewOnPeriodEnd: value.general.imsBillingRenewOnPeriodEnd,
          periodFeeInCents: value.general.imsBillingPriceInCents,
          // Set startDate to today if toggling on for the first time
          ...(isTogglingImsBillingOn && { startDate: new Date() }),
        }
      : {
          // Create new with defaults
          enabled: value.general.imsBillingEnabled,
          renewOnPeriodEnd: value.general.imsBillingRenewOnPeriodEnd,
          period: BillingPeriod.MONTHLY,
          periodFeeInCents: value.general.imsBillingPriceInCents,
          paymentProvider: PaymentProviderType.STRIPE,
          // Set startDate to today if enabling
          ...(isImsBillingNowEnabled && { startDate: new Date() }),
        };

    // Update or create forecastingBilling with required fields
    const existingForecastingBilling = team.settings.ims?.forecastingBilling;
    const forecastingBilling = existingForecastingBilling
      ? {
          ...existingForecastingBilling,
          // Override with form values
          enabled: value.general.forecastingBillingEnabled,
          periodFeeInCents: value.general.forecastingCustomPriceInCents,
          customPriceInCents: value.general.forecastingCustomPriceInCents,
          // Ensure required fields have defaults if missing from existing data
          period: existingForecastingBilling.period ?? BillingPeriod.MONTHLY,
          paymentProvider:
            existingForecastingBilling.paymentProvider ??
            PaymentProviderType.STRIPE,
          // Set startDate to today if toggling on for the first time
          ...(isTogglingForecastingBillingOn && { startDate: new Date() }),
        }
      : {
          // Create new with defaults
          enabled: value.general.forecastingBillingEnabled,
          renewOnPeriodEnd: true,
          period: BillingPeriod.MONTHLY,
          periodFeeInCents: value.general.forecastingCustomPriceInCents,
          paymentProvider: PaymentProviderType.STRIPE,
          customPriceInCents: value.general.forecastingCustomPriceInCents,
          // Set startDate to today if enabling
          ...(isForecastingBillingNowEnabled && { startDate: new Date() }),
        };

    await rpcClient.updateImsSettings({
      teamId: id,
      settings: {
        enabled: value.general.enabled,
        syncEnabled: value.general.syncEnabled,
        forecastingEnabled: value.general.forecastingEnabled,
        catalogEnabled: value.general.catalogEnabled,
        // Keep the persisted status read-only until the onboarding workflow
        // transitions it to pending after a successful start.
        status: sotToggled ? initialStatus : value.general.status,
        catalogSourceOfTruthStatus: value.general.catalogSourceOfTruthStatus,
        subscriptionBilling,
        forecastingBilling,
        orderSyncTemporal: value.general.orderSyncTemporal,
        handleNegativeOnHand: value.general.handleNegativeOnHand,
      },
    });

    await rpcClient.updateInventoryPushSettings({
      teamId: id,
      channels: inventoryPushChannels
        .filter((channel) => inventoryPushConnected[channel])
        .map((channel) => ({ channel, enabled: value.inventoryPush[channel] })),
    });

    // Save forecasting cost breakdown if there are buckets
    if (value.forecastingCostBreakdown.buckets.length > 0) {
      // Map regular buckets
      const bucketsToSave = value.forecastingCostBreakdown.buckets
        .filter((b) => Number(b.upperBound) > 0)
        .map((b) => ({
          upperBound: Number(b.upperBound),
          price: Number(b.price),
          currency: b.currency,
        }));

      // Add catch-all bucket with upperBound: -1 only if it has a price
      const catchAllPrice = Number(
        value.forecastingCostBreakdown.catchAllPrice,
      );
      if (catchAllPrice > 0) {
        bucketsToSave.push({
          upperBound: -1,
          price: catchAllPrice,
          currency: value.forecastingCostBreakdown.catchAllCurrency,
        });
      }

      const response = await rpcClient.saveForecastingCostBreakdown({
        teamId: id,
        buckets: bucketsToSave,
      });

      // Update form with new SKU counts from response
      const catchAllBucket = response.buckets.find((b) => b.upperBound === -1);
      const regularBuckets = response.buckets.filter((b) => b.upperBound > 0);

      const updatedBuckets = regularBuckets.map((b) => ({
        upperBound: b.upperBound.toString(),
        price: b.price.toString(),
        currency: b.currency,
        skuCount: b.skuCount,
      }));

      const updatedValue: ImsValue = {
        ...value,
        forecastingCostBreakdown: {
          buckets: updatedBuckets,
          catchAllSkuCount: catchAllBucket?.skuCount || 0,
          catchAllPrice: catchAllBucket?.price.toString() || "0",
          catchAllCurrency: catchAllBucket?.currency || "USD",
        },
      };

      // Update the input state with new SKU counts
      input.setValue(updatedValue);

      onSave(updatedValue);
    } else {
      onSave(value);
    }

    if (sotToggled) {
      await enableIms();
    }
  });

  const save: PageFormSave = useHandler(async (signal) => {
    if (sotToggled) {
      setShowSotConfirmation(true);
      return;
    }
    await doSave(signal);
  });

  const { general, forecastingCostBreakdown, inventoryPush } = input.inputs;

  const enableIms = useHandler(async () => {
    try {
      const result = await rpcClient.enableIms({ teamId: id });
      console.log("Started IMS onboarding workflow:", result.workflowId);
      toast(`IMS onboarding started. Workflow ID: ${result.workflowId}`, {
        variant: "success",
      });
      handleSourceOfTruthToggleChange(true);
      onSave(input.value);
    } catch (error) {
      console.error("Failed to enable IMS:", error);
      const message = error instanceof Error ? error.message : "Unknown error";
      toast(`Failed to start IMS onboarding: ${message}`, { variant: "error" });
      handleSourceOfTruthToggleChange(false);
    }
  });

  const handleOpenForecastingBreakdown = useHandler(() => {
    setShowForecastingModal(true);
  });

  const handleSourceOfTruthToggleChange = useHandler((toggled: boolean) => {
    general.inputs.status.setValue(
      toggled ? ImsStatus.Pending : ImsStatus.ReadOnly,
    );
  });

  // IMS requires source of truth, so declining source of truth turns IMS off.
  const cancelSourceOfTruth = useHandler(() => {
    setShowSotConfirmation(false);
    handleSourceOfTruthToggleChange(false);
    general.inputs.enabled.setValue(false);
  });

  return (
    <div className={teamCss.container}>
      <PageForm initial={value || imsDefault} input={input} save={save}>
        <div className={gridCss.grid}>
          <div className={classNames(gridCss.span12, gridCss.span6L)}>
            <div className={gridCss.grid}>
              <div className={gridCss.span12}>
                <ImsGeneralCard
                  imsBillingStartDate={imsBillingStartDate}
                  initialStatus={initialStatus}
                  input={general}
                  inventoryPush={inventoryPush}
                  inventoryPushConnected={inventoryPushConnected}
                  onOpenForecastingBreakdown={handleOpenForecastingBreakdown}
                  onSourceOfTruthToggleChange={handleSourceOfTruthToggleChange}
                  selfEnroll={selfEnroll}
                />
              </div>
              <div className={gridCss.span12}>
                <CatalogCard input={general} />
              </div>
              <div className={gridCss.span12}>
                <ShopifyBulkVariantEditCard teamId={id} />
              </div>
            </div>
          </div>
        </div>
      </PageForm>

      <RedoModal
        isOpen={showForecastingModal}
        onModalCloseRequested={() => setShowForecastingModal(false)}
        title="Forecasting Cost Breakdown"
      >
        <ForecastingCostBreakdownCard input={forecastingCostBreakdown} />
      </RedoModal>

      <RedoModal
        isOpen={showSotConfirmation}
        modalSize="sm"
        onModalCloseRequested={cancelSourceOfTruth}
        primaryButton={{
          text: "Yes, enable",
          onClick: async () => {
            setShowSotConfirmation(false);
            await doSave(new AbortController().signal);
          },
        }}
        secondaryButton={{ text: "Cancel", onClick: cancelSourceOfTruth }}
        subtitle="This will start the onboarding workflow to transition this team's inventory to use Redo as the source of truth. It may take several hours."
        theme="info"
        title="Enable Source of Truth?"
      >
        {input.value.inventoryPush.shopify ? (
          <Text fontSize="sm" fontWeight="semibold">
            Shopify inventory push is ON. Redo will begin pushing inventory to
            Shopify immediately after onboarding completes. We recommend keeping
            it off until the brand is ready to turn it on.
          </Text>
        ) : (
          <Text fontSize="sm" fontWeight="semibold" textColor="success">
            Shopify inventory push is OFF. Redo will not push inventory to
            Shopify until it is turned on. We recommend keeping it off until the
            brand is ready to turn it on.
          </Text>
        )}
      </RedoModal>
    </div>
  );
});
